import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ProductsService } from 'src/app/shared/service/product.service';
import Swal from 'sweetalert2';
import * as Notiflix from 'notiflix';

@Component({
  selector: 'app-teachers',
  templateUrl: './teachers.component.html',
  styleUrls: ['./teachers.component.scss']
})
export class TeachersComponent implements OnInit {

  // Variables para la tabla
  teachers: any[] = [];
  filteredTeachers: any[] = [];
  searchTerm: string = '';
  isLoading: boolean = false;
  totalCount: number = 0;
  totalPages: number = 0;

  // Variables para paginación
  currentPage: number = 1;
  itemsPerPage: number = 10;
  paginatedTeachers: any[] = [];
  
  // Variables adicionales para paginación de API
  apiLimit: number = 10;
  apiOffset: number = 0;
  itemsPerPageOptions: number[] = [5, 10, 15, 25, 50, 100];

  // Variables para ordenamiento
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  // Variables para el modal
  teacherForm: FormGroup;
  isSubmitting: boolean = false;

  // Exponer Math para usar en el template
  Math = Math;

  constructor(
    private modalService: NgbModal,
    private formBuilder: FormBuilder,
    private productService: ProductsService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadTeachers();
  }

  initializeForm(): void {
    this.teacherForm = this.formBuilder.group({
      firstName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
      lastName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]]
    });
  }

  loadTeachers(): void {
    this.isLoading = true;
    
    // Calcular offset como número de página (0-indexado)
    // Página 1 → offset=0, Página 2 → offset=1, Página 3 → offset=2
    this.apiOffset = this.currentPage - 1;
    
    console.log(`Cargando página ${this.currentPage}: limit=${this.apiLimit}, offset=${this.apiOffset}`);
    console.log(`URL resultante: /teachers?limit=${this.apiLimit}&offset=${this.apiOffset}`);
    
    // Llamada a la API /teachers con parámetros de paginación
    this.productService.getTeachers(this.apiLimit, this.apiOffset).subscribe({
      next: (response) => {
        console.log('Instructores cargados:', response);
        // La respuesta viene con la estructura { Count, Pages, List }
        this.teachers = response.List || response.data || response || [];
        this.filteredTeachers = [...this.teachers];
        this.totalCount = response.Count || this.teachers.length;
        this.totalPages = response.Pages || 1;
        
        // Aplicar ordenamiento si existe
        this.applySorting();
        this.updatePagination();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error al cargar instructores:', error);
        this.teachers = [];
        this.filteredTeachers = [];
        this.isLoading = false;
        
        // Solo mostrar error si no es un 404 (API vacía)
        if (error.status !== 404) {
          Swal.fire({
            icon: 'error',
            title: 'Error al cargar datos',
            text: 'No se pudieron cargar los instructores. Intenta de nuevo.',
            confirmButtonColor: '#dc3545'
          });
        }
      }
    });
  }

  filterTeachers(): void {
    if (!this.searchTerm.trim()) {
      this.filteredTeachers = [...this.teachers];
    } else {
      this.filteredTeachers = this.teachers.filter(teacher =>
        teacher.firstName?.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        teacher.lastName?.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        teacher.email?.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        teacher.phoneNumber?.includes(this.searchTerm)
      );
    }
    
    // Aplicar ordenamiento después del filtrado
    this.applySorting();
    // No cambiar página al filtrar localmente
    this.updatePagination();
  }

  // Método para actualizar la paginación local (solo para filtrado)
  updatePagination(): void {
    // Para filtrado local, usar itemsPerPage en lugar de apiLimit
    const startIndex = 0; // Mostrar todos los resultados filtrados
    const endIndex = this.filteredTeachers.length;
    this.paginatedTeachers = this.filteredTeachers.slice(startIndex, endIndex);
  }

  // Método para cambiar de página
  changePage(page: number): void {
    if (page >= 1 && page <= this.getTotalPages()) {
      this.currentPage = page;
      this.loadTeachers(); // Recargar datos con nueva página
    }
  }

  // Método para cambiar el límite de registros por página
  changeItemsPerPage(newLimit: number): void {
    this.apiLimit = newLimit;
    this.itemsPerPage = newLimit;
    this.currentPage = 1; // Resetear a la primera página
    this.loadTeachers(); // Recargar datos con nuevo límite
  }

  // Método para ir a una página específica
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadTeachers();
    }
  }

  // Obtener el número total de páginas desde la API
  getTotalPages(): number {
    return this.totalPages || Math.ceil(this.totalCount / this.apiLimit);
  }

  // Obtener array de números de página para mostrar
  getPageNumbers(): number[] {
    const totalPages = this.getTotalPages();
    const pages: number[] = [];
    
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const start = Math.max(1, this.currentPage - 2);
      const end = Math.min(totalPages, this.currentPage + 2);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  }

  // Método para recargar la tabla
  reloadTable(): void {
    this.loadTeachers();
  }

  // Método para ordenar por columna
  sortBy(column: string): void {
    if (this.sortColumn === column) {
      // Si ya está ordenado por esta columna, cambiar dirección
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      // Nueva columna, comenzar con ascendente
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    
    this.applySorting();
    this.currentPage = 1; // Resetear a la primera página
    this.updatePagination();
  }

  // Aplicar ordenamiento a los datos filtrados
  applySorting(): void {
    if (!this.sortColumn) return;

    this.filteredTeachers.sort((a, b) => {
      let valueA = this.getSortValue(a, this.sortColumn);
      let valueB = this.getSortValue(b, this.sortColumn);

      // Convertir a minúsculas si es string para comparación case-insensitive
      if (typeof valueA === 'string') valueA = valueA.toLowerCase();
      if (typeof valueB === 'string') valueB = valueB.toLowerCase();

      let comparison = 0;
      if (valueA > valueB) {
        comparison = 1;
      } else if (valueA < valueB) {
        comparison = -1;
      }

      return this.sortDirection === 'desc' ? comparison * -1 : comparison;
    });
  }

  // Obtener el valor para ordenar según la columna
  getSortValue(item: any, column: string): any {
    switch (column) {
      case 'name':
        return `${item.firstName || ''} ${item.lastName || ''}`.trim();
      case 'email':
        return item.email || '';
      case 'phoneNumber':
        return item.phoneNumber || '';
      case 'active':
        return item.active ? 1 : 0; // Activo primero
      case 'created_at':
        return new Date(item.created_at || 0).getTime();
      default:
        return '';
    }
  }

  // Obtener la clase CSS para el icono de ordenamiento
  getSortIcon(column: string): string {
    if (this.sortColumn !== column) {
      return 'fa-sort text-muted'; // Sin ordenar
    }
    return this.sortDirection === 'asc' ? 'fa-sort-up text-primary' : 'fa-sort-down text-primary';
  }

  // Método para ir a la primera página
  goToFirstPage(): void {
    if (this.currentPage !== 1) {
      this.currentPage = 1;
      this.loadTeachers();
    }
  }

  // Método para ir a la última página
  goToLastPage(): void {
    const lastPage = this.getTotalPages();
    if (this.currentPage !== lastPage) {
      this.currentPage = lastPage;
      this.loadTeachers();
    }
  }

  openAddTeacherModal(content: any): void {
    this.teacherForm.reset();
    this.modalService.open(content, { 
      size: 'md',
      backdrop: 'static',
      keyboard: false
    });
  }

  closeModal(): void {
    this.modalService.dismissAll();
    this.teacherForm.reset();
  }

  onSubmit(): void {
    if (this.teacherForm.valid) {
      this.isSubmitting = true;

      const formData = this.teacherForm.value;
      const requestBody = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber
      };

      // Llamada a la API /teachers
      this.productService.createInstructor(requestBody).subscribe({
        next: (response) => {
          console.log('Instructor registrado exitosamente:', response);
          
          Swal.fire({
            icon: 'success',
            title: '¡Instructor Registrado!',
            text: 'El instructor ha sido registrado exitosamente.',
            confirmButtonColor: '#007bff'
          });

          this.closeModal();
          this.loadTeachers(); // Recargar la lista
          this.isSubmitting = false;
        },
        error: (error) => {
          console.error('Error al registrar instructor:', error);
          
          let errorMessage = 'Hubo un problema al registrar el instructor. Inténtalo nuevamente.';
          
          // Manejar errores específicos
          if (error.status === 400 && error.error?.message) {
            errorMessage = error.error.message;
          } else if (error.status === 409) {
            errorMessage = 'Este email ya está registrado.';
          }
          
          Swal.fire({
            icon: 'error',
            title: 'Error al Registrar',
            text: errorMessage,
            confirmButtonColor: '#dc3545'
          });

          this.isSubmitting = false;
        }
      });
    } else {
      // Marcar todos los campos como tocados para mostrar errores
      Object.keys(this.teacherForm.controls).forEach(key => {
        this.teacherForm.get(key)?.markAsTouched();
      });
    }
  }

  // Getters para facilitar la validación en el template
  get firstName() { return this.teacherForm.get('firstName'); }
  get lastName() { return this.teacherForm.get('lastName'); }
  get email() { return this.teacherForm.get('email'); }
  get phoneNumber() { return this.teacherForm.get('phoneNumber'); }

  // Método para formatear el número de teléfono
  formatPhoneNumber(phone: string): string {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phone;
  }

  // Método para eliminar instructor (para futuro uso)
  deleteTeacher(teacherId: string): void {
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        // Aquí irá la lógica para eliminar el instructor
        console.log('Eliminar instructor con ID:', teacherId);
      }
    });
  }
}
